import { z } from 'zod';
const coerceBoolean = (value) => {
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'yes', 'on'].includes(normalized))
            return true;
        if (['false', '0', 'no', 'off'].includes(normalized))
            return false;
    }
    return value;
};
export const envSchema = z.object({
    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
    API_URL: z.string().url().optional(),
    WEB_URL: z.string().url().optional(),
    MONGO_URI: z.string().min(1, 'MONGO_URI is required'),
    REDIS_URL: z.string().min(1, 'REDIS_URL is required'),
    REDIS_TLS_CA: z.string().optional(),
    REDIS_TLS_CA_PATH: z.string().optional(),
    GCP_BUCKET: z.string().min(1, 'GCP_BUCKET is required'),
    GCP_UPLOADS_PENDING_BUCKET: z.string().min(1, 'GCP_UPLOADS_PENDING_BUCKET is required'),
    GCP_UPLOADS_READY_BUCKET: z.string().min(1, 'GCP_UPLOADS_READY_BUCKET is required'),
    GCP_UPLOADS_QUARANTINE_BUCKET: z.string().min(1, 'GCP_UPLOADS_QUARANTINE_BUCKET is required'),
    UPLOAD_DIRECT_MAX_BYTES: z.coerce
        .number()
        .int()
        .positive()
        .default(300 * 1024 * 1024),
    UPLOAD_DIRECT_SIGNED_URL_TTL_SECONDS: z.coerce.number().int().positive().default(900),
    UPLOAD_SCANNER_ENABLED: z
        .preprocess((value) => {
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (['true', '1', 'yes', 'on'].includes(normalized)) {
                return true;
            }
            if (['false', '0', 'no', 'off'].includes(normalized)) {
                return false;
            }
        }
        return value;
    }, z.boolean())
        .default(true),
    UPLOAD_MODERATION_ENABLED: z.preprocess(coerceBoolean, z.boolean()).default(true),
    FIREBASE_PROJECT_ID: z.string().min(1, 'FIREBASE_PROJECT_ID is required'),
    FIREBASE_CLIENT_EMAIL: z.string().email('FIREBASE_CLIENT_EMAIL must be a valid email'),
    FIREBASE_PRIVATE_KEY: z.string().min(1, 'FIREBASE_PRIVATE_KEY is required'),
    FAL_API_KEY: z.string().min(1, 'FAL_API_KEY is required'),
    REPLICATE_API_TOKEN: z.string().min(1, 'REPLICATE_API_TOKEN is required'),
    GEMINI_API_KEY: z.string().min(1, 'GEMINI_API_KEY is required'),
    GEMINI_ASSISTANT_MODEL: z.string().optional(),
    GEMINI_MOODBOARD_TIMEOUT_MS: z.coerce.number().int().positive().default(180_000),
    GEMINI_ANALYSIS_TIMEOUT_MS: z.coerce.number().int().positive().default(60_000),
    ASSISTANT_SHORTS_ENABLED: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_SHORTS_CROP_ENGINE: z.enum(['gemini', 'clipsai']).default('clipsai'),
    ASSISTANT_CLIPSAI_SCRIPT_PATH: z.string().optional(),
    ASSISTANT_CLIPSAI_PYTHON_PATH: z.string().optional(),
    ASSISTANT_CLIPSAI_TIMEOUT_MS: z.coerce.number().int().positive().default(900_000),
    ASSISTANT_CLIPSAI_DIARIZE_MODEL: z.string().optional(),
    ASSISTANT_CLIPSAI_LOG_IO: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_PROVIDER: z.enum(['openai', 'claude']).default('openai'),
    ASSISTANT_BILLING_ENABLED: z.preprocess(coerceBoolean, z.boolean()).default(true),
    ASSISTANT_BILLING_CREDITS_PER_USD: z.coerce.number().positive().default(100),
    ASSISTANT_BILLING_INPUT_TOKENS_PER_CREDIT: z.coerce.number().positive().default(2500),
    ASSISTANT_BILLING_OUTPUT_TOKENS_PER_CREDIT: z.coerce.number().positive().default(500),
    ASSISTANT_BILLING_MIN_CREDITS_PER_RUN: z.coerce.number().int().nonnegative().default(1),
    ANTHROPIC_API_KEY: z.string().optional(),
    ASSISTANT_CLAUDE_MODEL: z.string().optional(),
    ASSISTANT_CLAUDE_MAX_TURNS: z.coerce.number().int().positive().optional(),
    ASSISTANT_CLAUDE_MAX_BUDGET_USD: z.coerce.number().positive().optional(),
    ASSISTANT_CLAUDE_CWD: z.string().optional(),
    ASSISTANT_CLAUDE_PROCESS_BACKEND: z.enum(['local', 'e2b']).default('local'),
    ASSISTANT_CLAUDE_PERSIST_SESSION: z.preprocess(coerceBoolean, z.boolean()).default(true),
    ASSISTANT_CLAUDE_NATIVE_TOOLS: z.string().optional(),
    ASSISTANT_CLAUDE_SETTING_SOURCES: z.string().optional(),
    ASSISTANT_CLAUDE_SANDBOX_ENABLED: z.preprocess(coerceBoolean, z.boolean()).default(true),
    ASSISTANT_CLAUDE_SANDBOX_AUTO_ALLOW_BASH: z.preprocess(coerceBoolean, z.boolean()).default(true),
    ASSISTANT_CLAUDE_ALLOW_UNSANDBOXED_COMMANDS: z
        .preprocess(coerceBoolean, z.boolean())
        .default(true),
    ASSISTANT_CLAUDE_UNSANDBOXED_COMMAND_ALLOWLIST: z.string().optional(),
    ASSISTANT_CLAUDE_ALLOWED_WEB_DOMAINS: z.string().optional(),
    E2B_API_KEY: z.string().optional(),
    ASSISTANT_E2B_TEMPLATE: z.string().optional(),
    ASSISTANT_E2B_CWD: z.string().default('/workspace/services/api/assistant-runtime'),
    ASSISTANT_E2B_REPO_ROOT: z.string().default('/workspace'),
    ASSISTANT_E2B_CLAUDE_BINARY: z.string().default('claude'),
    ASSISTANT_E2B_SANDBOX_TIMEOUT_MS: z.coerce
        .number()
        .int()
        .positive()
        .default(60 * 60 * 1000),
    ASSISTANT_E2B_COMMAND_TIMEOUT_MS: z.coerce
        .number()
        .int()
        .positive()
        .default(60 * 60 * 1000),
    ASSISTANT_E2B_REQUEST_TIMEOUT_MS: z.coerce.number().int().positive().default(20_000),
    ASSISTANT_E2B_AUTO_PAUSE: z.preprocess(coerceBoolean, z.boolean()).default(true),
    ASSISTANT_E2B_ALLOW_INTERNET_ACCESS: z.preprocess(coerceBoolean, z.boolean()).default(true),
    ASSISTANT_E2B_SECURE: z.preprocess(coerceBoolean, z.boolean()).default(true),
    OPENAI_API_KEY: z.string().min(1, 'OPENAI_API_KEY is required'),
    ASSISTANT_RUNTIME_TOOLS_ENABLED: z.preprocess(coerceBoolean, z.boolean()).default(true),
    ASSISTANT_RUNTIME_TOOL_PROFILE: z.enum(['video', 'full']).default('video'),
    ASSISTANT_RUNTIME_AUTO_RENDER: z.preprocess(coerceBoolean, z.boolean()).default(true),
    ASSISTANT_RUNTIME_REPO_ROOT: z.string().optional(),
    ASSISTANT_RUNTIME_REMOTION_APP_PATH: z.string().optional(),
    ASSISTANT_RUNTIME_ALLOW_OUTSIDE_REPO: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_RUNTIME_COMMAND_TIMEOUT_MS: z.coerce.number().int().positive().default(900_000),
    ASSISTANT_RUNTIME_COMMAND_ALLOWED_DIRS: z.string().optional(),
    ASSISTANT_RUNTIME_VIDEO_ALLOWED_COMMANDS: z.string().optional(),
    ASSISTANT_RUNTIME_OUTPUT_PREFIX: z.string().default('assistant/dev-outputs'),
    // Backward-compatible aliases for legacy configs.
    ASSISTANT_DEV_MODE: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_DEV_TOOL_PROFILE: z.enum(['video', 'full']).default('video'),
    ASSISTANT_DEV_AUTO_RENDER: z.preprocess(coerceBoolean, z.boolean()).default(true),
    ASSISTANT_DEV_REPO_ROOT: z.string().optional(),
    ASSISTANT_DEV_REMOTION_APP_PATH: z.string().optional(),
    ASSISTANT_DEV_ALLOW_OUTSIDE_REPO: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_DEV_COMMAND_TIMEOUT_MS: z.coerce.number().int().positive().default(900_000),
    ASSISTANT_DEV_COMMAND_ALLOWED_DIRS: z.string().optional(),
    ASSISTANT_DEV_VIDEO_ALLOWED_COMMANDS: z.string().optional(),
    ASSISTANT_DEV_OUTPUT_PREFIX: z.string().default('assistant/dev-outputs'),
    ASSISTANT_REMOTION_SKILL_PATH: z.string().optional(),
    ASSISTANT_REMOTION_SKILL_MAX_BYTES: z.coerce.number().int().positive().default(300_000),
    ASSISTANT_AUTO_MEDIA_ANALYSIS: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_AUTO_CONTINUE_ENABLED: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_CONTINUATION_MAX_ATTEMPTS: z.coerce.number().int().positive().default(6),
    ASSISTANT_CONTINUATION_RETRY_DELAY_MS: z.coerce.number().int().positive().default(5_000),
    ASSISTANT_CONTINUATION_REQUEST_TIMEOUT_MS: z.coerce.number().int().positive().default(15_000),
    ASSISTANT_CONTINUATION_SECRET: z.string().optional(),
    ASSISTANT_INTERNAL_API_URL: z.string().url().optional(),
    ELEVENLABS_API_KEY: z.string().min(1, 'ELEVENLABS_API_KEY is required'),
    MOYASAR_SECRET_KEY: z.string().min(1, 'MOYASAR_SECRET_KEY is required'),
    MOYASAR_PUBLIC_KEY: z.string().min(1, 'MOYASAR_PUBLIC_KEY is required'),
    MOYASAR_WEBHOOK_SECRET: z.string().min(1, 'MOYASAR_WEBHOOK_SECRET is required'),
    POSTHOG_ENABLED: z.preprocess(coerceBoolean, z.boolean()).default(false),
    POSTHOG_KEY: z.string().optional(),
    POSTHOG_HOST: z.string().url().default('https://eu.i.posthog.com'),
    RESEND_API_KEY: z.string().optional(),
    ASSISTANT_LOG_EVENTS: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_LOG_EVENT_DELTAS: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_LOG_CLAUDE_SDK_EVENTS: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_LOG_CLAUDE_SDK_EVENT_DELTAS: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_LOG_STREAM_TRANSLATION: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_LOG_STREAM_TRANSLATION_DELTAS: z.preprocess(coerceBoolean, z.boolean()).default(false),
    ASSISTANT_DOWNLOAD_TIMEOUT_MS: z.coerce.number().int().positive().default(1_800_000),
    ASSISTANT_DOWNLOAD_MAX_BYTES: z.coerce
        .number()
        .int()
        .positive()
        .default(2 * 1024 * 1024 * 1024),
    ASSISTANT_DOWNLOAD_ALLOWED_HOSTS: z.string().optional(),
    ASSISTANT_DOWNLOAD_YTDLP_HOSTS: z.string().optional(),
    YTDLP_PATH: z.string().optional(),
    YTDLP_JS_RUNTIMES: z.string().default('node'),
    YTDLP_REMOTE_COMPONENTS: z.string().default('ejs:github'),
    YTDLP_COOKIES_FILE: z.string().optional(),
    YTDLP_AUTO_UPDATE: z.preprocess(coerceBoolean, z.boolean()).default(true),
    YTDLP_UPDATE_INTERVAL_HOURS: z.coerce.number().int().positive().default(6),
    METRICS_BEARER_TOKEN: z.string().optional(),
    LANGSMITH_TRACING: z.preprocess(coerceBoolean, z.boolean()).default(false),
    LANGSMITH_API_KEY: z.string().optional(),
    LANGSMITH_ENDPOINT: z.string().url().optional(),
    LANGSMITH_PROJECT: z.string().optional(),
    LANGSMITH_WORKSPACE_ID: z.string().optional(),
    LANGSMITH_DEBUG: z.preprocess(coerceBoolean, z.boolean()).default(false),
    SESSION_SECRET: z.string().min(32, 'SESSION_SECRET must be at least 32 characters').optional(),
    ACCOUNT_DELETION_GRACE_PERIOD_HOURS: z.coerce.number().int().positive().default(72),
    OUTBOUND_EMAIL_FROM: z.string().email().optional(),
    SUPPORT_EMAIL: z.string().email().optional(),
    CORS_ALLOWED_ORIGINS: z.string().optional(),
    PROVIDER_RATE_LIMITS: z.string().optional(),
    STORYBOARD_IMAGE_RATE_LIMIT_POINTS: z.coerce.number().int().positive().default(10),
    STORYBOARD_IMAGE_RATE_LIMIT_DURATION: z.coerce.number().int().positive().default(3600),
    STORYBOARD_VIDEO_RATE_LIMIT_POINTS: z.coerce.number().int().positive().default(6),
    STORYBOARD_VIDEO_RATE_LIMIT_DURATION: z.coerce.number().int().positive().default(3600),
    STORYBOARD_BULK_RATE_LIMIT_POINTS: z.coerce.number().int().positive().default(10),
    STORYBOARD_BULK_RATE_LIMIT_DURATION: z.coerce.number().int().positive().default(900),
    SIGNED_URL_DEFAULT_TTL_SECONDS: z.coerce.number().int().positive().default(900),
    SIGNED_URL_MAX_TTL_SECONDS: z.coerce.number().int().positive().default(86_400),
    SIGNED_URL_TTL_SECONDS: z.coerce.number().int().positive().default(300),
    UPLOAD_MODERATION_URL_TTL_SECONDS: z.coerce.number().int().positive().default(300),
    EXTERNAL_ASSET_DOWNLOAD_HOSTS: z.string().optional(),
    EXTERNAL_ASSET_DOWNLOAD_TIMEOUT_MS: z.coerce.number().int().positive().optional(),
    EXTERNAL_ASSET_DOWNLOAD_MAX_BYTES: z.coerce.number().int().positive().optional(),
    DEFAULT_USER_EMAIL: z.string().email().optional(),
    DEFAULT_USER_NAME: z.string().optional(),
    DEV_ADMIN_BYPASS_SECRET: z
        .string()
        .min(12, 'DEV_ADMIN_BYPASS_SECRET must be at least 12 characters')
        .optional(),
    DEV_ADMIN_EMAIL: z.string().email().optional(),
    DEV_ADMIN_NAME: z.string().optional(),
    DEV_ADMIN_USER_ID: z.string().min(1).optional(),
    DEV_EMAIL_LOGIN_ALLOWED: z
        .preprocess(coerceBoolean, z.boolean())
        .default(false)
        .describe('Enable dev-only email login bypass (non-production only)'),
    DEV_ADMIN_INITIAL_CREDITS: z.coerce.number().int().nonnegative().default(0),
    DEFAULT_TRIAL_CREDITS: z.coerce.number().int().nonnegative().default(0),
    TRIAL_DURATION_DAYS: z.coerce.number().int().nonnegative().default(0),
    GUEST_DEFAULT_CREDITS: z.coerce.number().int().nonnegative().default(0),
    GUEST_DURATION_DAYS: z.coerce.number().int().nonnegative().default(0),
    GCS_BUCKET_STORYBOARDS: z.string().optional(),
    UPLOAD_SCAN_SERVICE_URL: z.string().url().optional(),
    UPLOAD_SCAN_SERVICE_TOKEN: z.string().optional(),
    UPLOAD_SCAN_TIMEOUT_MS: z.coerce.number().int().positive().default(180_000),
    FFMPEG_PATH: z.string().optional(),
    FFPROBE_PATH: z.string().optional(),
    FFMPEG_TEMP_DIR: z.string().default('/tmp/athwab-ffmpeg'),
    FFMPEG_STITCH_TIMEOUT_MS: z.coerce.number().int().positive().default(600_000),
    FFMPEG_CLEANUP_ON_SUCCESS: z
        .preprocess((value) => {
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (['true', '1', 'yes', 'on'].includes(normalized)) {
                return true;
            }
            if (['false', '0', 'no', 'off'].includes(normalized)) {
                return false;
            }
        }
        return value;
    }, z.boolean())
        .default(true),
    ASSISTANT_FACE_MODEL_URL: z.string().url().optional(),
});
const normalizeInput = (input) => Object.fromEntries(Object.entries(input).map(([key, value]) => [key, value === '' ? undefined : value]));
export const parseEnv = (input) => {
    const result = envSchema.safeParse(normalizeInput(input));
    if (!result.success) {
        const issues = result.error.errors
            .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
            .join(', ');
        throw new Error(`Invalid environment configuration: ${issues}`);
    }
    const parsed = result.data;
    if (parsed.NODE_ENV === 'production' &&
        (parsed.DEV_EMAIL_LOGIN_ALLOWED ||
            parsed.DEV_ADMIN_BYPASS_SECRET ||
            parsed.DEV_ADMIN_EMAIL ||
            parsed.DEV_ADMIN_USER_ID)) {
        throw new Error('Invalid environment configuration: dev authentication settings are not allowed in production.');
    }
    return parsed;
};
//# sourceMappingURL=env.js.map
